import React, { useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';

// Simplified constellation background for legal pages
const ConstellationBackground = ({ theme }) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const nodesRef = useRef([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let width = window.innerWidth;
    let height = document.documentElement.scrollHeight;

    const resize = () => {
      width = window.innerWidth;
      height = Math.max(document.documentElement.scrollHeight, window.innerHeight);
      canvas.width = width;
      canvas.height = height;
      initNodes();
    };

    const initNodes = () => {
      const nodeCount = Math.floor((width * height) / 25000);
      nodesRef.current = [];
      for (let i = 0; i < nodeCount; i++) {
        nodesRef.current.push({
          x: Math.random() * width,
          y: Math.random() * height,
          vx: (Math.random() - 0.5) * 0.15,
          vy: (Math.random() - 0.5) * 0.15,
          radius: Math.random() * 1.5 + 0.5,
          pulse: Math.random() * Math.PI * 2,
        });
      }
    };

    const draw = () => {
      const isDark = theme === 'dark';
      ctx.fillStyle = isDark
        ? 'rgba(10, 22, 40, 0.03)'
        : 'rgba(239, 246, 255, 0.03)';
      ctx.fillRect(0, 0, width, height);

      const nodeColor = isDark ? 'rgba(147, 197, 253, ' : 'rgba(59, 130, 246, ';
      const lineColor = isDark ? 'rgba(147, 197, 253, ' : 'rgba(59, 130, 246, ';

      nodesRef.current.forEach((node, i) => {
        node.x += node.vx;
        node.y += node.vy;
        node.pulse += 0.01;

        if (node.x < 0 || node.x > width) node.vx *= -1;
        if (node.y < 0 || node.y > height) node.vy *= -1;

        const pulseScale = 1 + Math.sin(node.pulse) * 0.2;
        const radius = node.radius * pulseScale;

        ctx.beginPath();
        ctx.arc(node.x, node.y, radius, 0, Math.PI * 2);
        ctx.fillStyle = nodeColor + (0.3 + Math.sin(node.pulse) * 0.1) + ')';
        ctx.fill();

        nodesRef.current.slice(i + 1).forEach(other => {
          const dx = other.x - node.x;
          const dy = other.y - node.y;
          const dist = Math.sqrt(dx * dx + dy * dy);

          if (dist < 120) {
            ctx.beginPath();
            ctx.moveTo(node.x, node.y);
            ctx.lineTo(other.x, other.y);
            ctx.strokeStyle = lineColor + (0.08 * (1 - dist / 120)) + ')';
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        });
      });

      animationRef.current = requestAnimationFrame(draw);
    };

    resize();
    window.addEventListener('resize', resize);
    draw();

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationRef.current);
    };
  }, [theme]);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none"
      style={{ zIndex: 0 }}
    />
  );
};

// Section component with staggered animation
const Section = ({ number, title, children, delay = 0 }) => (
  <section
    className="mb-10 animate-fade-in"
    style={{ animationDelay: `${delay}ms` }}
  >
    <div className="flex items-start gap-4 mb-4">
      <span className="flex-shrink-0 w-10 h-10 rounded-xl bg-alpha-gradient flex items-center justify-center text-white font-bold text-sm shadow-alpha">
        {number}
      </span>
      <h2 className="text-2xl font-bold text-silver-800 dark:text-white pt-1">
        {title}
      </h2>
    </div>
    <div className="pl-14 text-silver-600 dark:text-silver-400 leading-relaxed space-y-4">
      {children}
    </div>
  </section>
);

const TermsOfService = ({ theme }) => {
  const navigate = useNavigate();

  return (
    <div className={`relative min-h-screen ${theme === 'dark' ? 'dark' : ''}`}>
      {/* Background */}
      <div className="fixed inset-0 bg-gradient-to-br from-alpha-50 via-white to-silver-100 dark:from-navy-950 dark:via-navy-900 dark:to-alpha-950 transition-colors duration-500" />
      <ConstellationBackground theme={theme} />

      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white/80 dark:bg-navy-900/80 backdrop-blur-xl border-b border-silver-200/50 dark:border-silver-700/20">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-silver-600 dark:text-silver-400 hover:text-alpha-500 dark:hover:text-alpha-400 transition-colors font-medium"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>

          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="HeyAlphas" className="w-8 h-8" />
            <span className="text-xl font-black tracking-tight text-silver-800 dark:text-white">
              Hey<span className="text-alpha-500">Alphas</span>
            </span>
          </Link>
        </div>
      </nav>

      {/* Main Content */}
      <main className="relative z-10 max-w-4xl mx-auto px-6 py-12">
        {/* Header Card */}
        <div className="text-center mb-12 animate-slide-up">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-alpha-gradient shadow-alpha-lg mb-6">
            <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h1 className="text-4xl md:text-5xl font-black text-silver-800 dark:text-white mb-3">
            Terms of <span className="text-alpha-500">Service</span>
          </h1>
          <p className="text-silver-500 dark:text-silver-400">
            Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </p>
        </div>

        {/* Content Card */}
        <div className="relative bg-white/70 dark:bg-navy-800/50 backdrop-blur-xl rounded-3xl border border-silver-200/50 dark:border-silver-700/20 p-8 md:p-12 shadow-xl">
          {/* Decorative corner accents */}
          <div className="absolute top-6 right-6 w-12 h-12 border-t-2 border-r-2 border-alpha-300/30 dark:border-alpha-500/20 rounded-tr-2xl" />
          <div className="absolute bottom-6 left-6 w-12 h-12 border-b-2 border-l-2 border-alpha-300/30 dark:border-alpha-500/20 rounded-bl-2xl" />

          <Section number="1" title="Agreement to Terms" delay={100}>
            <p>
              By accessing and using HeyAlphas ("the Service"), you agree to be bound by these Terms of Service ("Terms").
              If you disagree with any part of these terms, then you may not access the Service. These Terms apply to all
              visitors, users, and others who access or use the Service.
            </p>
          </Section>

          <Section number="2" title="Description of Service" delay={150}>
            <p>
              HeyAlphas is a mobile application that helps users discover cryptocurrency alpha opportunities. The Service
              offers market tracking, AI-powered insights from Alphie, watchlist management, and analytics tools to help
              users identify high-potential opportunities before they trend.
            </p>
            <div className="bg-gradient-to-r from-red-500/10 to-orange-500/10 dark:from-red-500/20 dark:to-orange-500/20 border border-red-300/50 dark:border-red-500/30 rounded-2xl p-5">
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-red-500/20 dark:bg-red-500/30 flex items-center justify-center">
                  <svg className="w-4 h-4 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div>
                  <p className="font-semibold text-red-700 dark:text-red-300 mb-1">Important Disclaimer</p>
                  <p className="text-sm text-red-600/90 dark:text-red-300/80">
                    All information, data, and insights provided through HeyAlphas are for informational purposes only
                    and do not constitute investment advice. HeyAlphas does not recommend that any cryptocurrency
                    should be bought, sold, or held by you. Always conduct your own research and consult with qualified
                    financial advisors before making any investment decisions.
                  </p>
                </div>
              </div>
            </div>
          </Section>

          <Section number="3" title="User Accounts and Authentication" delay={200}>
            <p>
              To access certain features of the Service, you may be required to create an account via Google OAuth or Sign in with Apple.
              You are responsible for maintaining the confidentiality of your account and for all activities that occur
              under your account. You agree to notify us immediately of any unauthorized use of your account.
            </p>
          </Section>

          <Section number="4" title="Premium Subscriptions" delay={250}>
            <p>HeyAlphas offers premium subscription services that provide additional features including:</p>
            <ul className="list-none space-y-2 ml-0">
              {[
                'Ad-free experience',
                'Priority customer support',
                'Early access to new features',
                'Advanced trend predictions',
                'Premium-only market reports'
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-alpha-100 dark:bg-alpha-900/50 flex items-center justify-center mt-0.5">
                    <svg className="w-3 h-3 text-alpha-600 dark:text-alpha-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p>
              Subscription fees are processed through Stripe or Apple and are billed on a recurring basis. You may cancel
              your subscription at any time, but refunds are generally not provided for partial subscription periods.
            </p>
          </Section>

          <Section number="5" title="Prohibited Uses" delay={300}>
            <p>You may not use the Service:</p>
            <ul className="list-none space-y-2 ml-0">
              {[
                'For any unlawful purpose or to solicit others to perform illegal activities',
                'To violate any international, federal, provincial, or state regulations, rules, laws, or local ordinances',
                'To transmit any advertising or promotional material without our prior written consent',
                'To impersonate or attempt to impersonate the Company, an employee, or any other person',
                'In any way that infringes upon the rights of others, or is illegal, threatening, or harmful',
                'To engage in any conduct that restricts anyone\'s use or enjoyment of the Service'
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-silver-100 dark:bg-silver-800/50 flex items-center justify-center mt-0.5">
                    <svg className="w-3 h-3 text-silver-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </Section>

          <Section number="6" title="Intellectual Property Rights" delay={350}>
            <p>
              The Service and its original content, features, and functionality are and will remain the exclusive property
              of HeyAlphas and its licensors. The Service is protected by copyright, trademark, and other laws. Our trademarks
              and trade dress may not be used in connection with any product or service without our prior written consent.
            </p>
          </Section>

          <Section number="7" title="Data Collection and Privacy" delay={400}>
            <p>
              Your privacy is important to us. We collect and use information about you in accordance with our Privacy Policy.
              By using the Service, you consent to the collection and use of information as outlined in our Privacy Policy.
            </p>
            <p>
              We may collect usage data, authentication information through Google OAuth, Apple Sign-in and subscription-related information
              through Stripe and Apple to provide and improve our services.
            </p>
          </Section>

          <Section number="8" title="Disclaimer of Warranties" delay={450}>
            <div className="bg-silver-100/50 dark:bg-navy-700/30 rounded-2xl p-5 border border-silver-200/50 dark:border-silver-700/30">
              <p className="uppercase text-sm font-medium tracking-wide">
                The Service is provided on an "as is" and "as available" basis. HeyAlphas expressly disclaims all warranties
                of any kind, whether express, implied, or statutory, including but not limited to the implied warranties of
                merchantability, fitness for a particular purpose, title, and non-infringement.
              </p>
            </div>
            <p>
              HeyAlphas makes no warranty that the Service will meet your requirements, be available on an uninterrupted,
              timely, secure, or error-free basis, or that the information provided will be accurate, reliable, or complete.
            </p>
          </Section>

          <Section number="9" title="Limitation of Liability" delay={500}>
            <div className="bg-silver-100/50 dark:bg-navy-700/30 rounded-2xl p-5 border border-silver-200/50 dark:border-silver-700/30">
              <p className="uppercase text-sm font-medium tracking-wide">
                In no event will HeyAlphas, its affiliates, agents, directors, employees, suppliers, or licensors be liable
                for damages of any kind, under any legal theory, arising out of or in connection with your use, or inability
                to use, the Service, including any direct, indirect, incidental, special, consequential, or punitive damages.
              </p>
            </div>
          </Section>

          <Section number="10" title="Service Modifications and Termination" delay={550}>
            <p>
              We reserve the right to modify or discontinue, temporarily or permanently, the Service or any features or
              portions thereof without prior notice. You agree that we will not be liable for any modification, suspension,
              or discontinuance of the Service.
            </p>
            <p>
              We may terminate your access to all or part of the Service at any time, with or without cause, with or without notice.
            </p>
          </Section>

          <Section number="11" title="Governing Law" delay={600}>
            <p>
              These Terms shall be interpreted and governed by the laws of the jurisdiction in which HeyAlphas operates,
              without regard to its conflict of law provisions. Any disputes arising under these Terms shall be subject
              to the exclusive jurisdiction of the courts in that jurisdiction.
            </p>
          </Section>

          <Section number="12" title="Changes to Terms" delay={650}>
            <p>
              We reserve the right to modify these Terms at any time. We will provide notice of material changes by
              updating the "Last updated" date at the top of these Terms. Your continued use of the Service after such
              modifications will constitute acknowledgment and agreement to the modified Terms.
            </p>
          </Section>

          <Section number="13" title="Contact Information" delay={700}>
            <p>
              If you have any questions about these Terms of Service, please contact us through the Service or via
              our support channels.
            </p>
            <div className="bg-alpha-50/50 dark:bg-alpha-900/20 rounded-2xl p-5 border border-alpha-200/50 dark:border-alpha-700/30">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 rounded-lg bg-alpha-gradient flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <span className="font-semibold text-silver-800 dark:text-white">Get in Touch</span>
              </div>
              <p className="text-sm">
                Email us at <a href="mailto:support@woleh.com" className="text-alpha-600 dark:text-alpha-400 hover:underline font-medium">support@woleh.com</a>
              </p>
            </div>
          </Section>
        </div>

        {/* Footer navigation */}
        <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4 text-sm text-silver-500 dark:text-silver-400">
          <Link to="/privacy" className="hover:text-alpha-500 dark:hover:text-alpha-400 transition-colors">
            Privacy Policy
          </Link>
          <span className="hidden sm:inline">•</span>
          <Link to="/support" className="hover:text-alpha-500 dark:hover:text-alpha-400 transition-colors">
            Support
          </Link>
          <span className="hidden sm:inline">•</span>
          <Link to="/" className="hover:text-alpha-500 dark:hover:text-alpha-400 transition-colors">
            Back to Home
          </Link>
        </div>
      </main>
    </div>
  );
};

export default TermsOfService;
